import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import {
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { DateRangePicker } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { useOrderStatus } from "hooks/order/useOrder";
import { useHubMultiSelectOptions } from "components/shared-components/HubMultiSelectFilter";
import { DVVC_LABELS, getTrendLineColorForLabel } from "./dashboardChartUtils";
import KpiPerformanceBarChart from "./KpiPerformanceBarChart";
import "./OrderManagementDashboard/OrderManagementDashboard.scss";
import {
  buildTimelineChartOptions,
  computeNiceYAxisBounds,
  TIMELINE_CHART_HEIGHT_PX,
} from "./OrderManagementDashboard/chartConfig";
import { useDashboardDailyTimelineStats } from "./OrderManagementDashboard/useDashboardDailyTimelineStats";
import { MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS } from "./OrderManagementDashboard/normalizeDashboardTimelineStats";
import {
  FALLBACK_STATUS_FILTER_OPTIONS,
  INITIAL_STATE,
  LEGEND_COLS,
  filterDateRange,
  formatDateLabel,
  formatHoverGuideValue,
  getChartGridTooltipUnionBounds,
  getLast7DaysRange,
  getLastNDaysRange,
  getTodayIsoDate,
  isPointInsideScreenBounds,
  isStatusChartKeyHiddenByDefault,
  getDashboardHubFilterIds,
  resolveDashboardOrderTypeParam,
  resolveTimelineMilestoneIndex,
} from "./OrderManagementDashboard/orderManagementDashboardUtils";

const Dashboard = () => {
  const handleNavigateDetails = useCallback(({ key, date, type }) => {
    const params = new URLSearchParams({
      dashboard_key: key || "",
      dashboard_date: date || "",
      dashboard_type: type || "status",
    });
    window.open(`${window.location.origin}/manage-orders/?${params.toString()}`, "_blank")?.focus();
  }, []);

  const [state, setState] = useState(INITIAL_STATE);
  const [focusedInput, setFocusedInput] = useState(null);
  const chartWrapperRef = useRef(null);
  const timelineMountRef = useRef(null);
  const timelineModelRef = useRef({ categories: [], keys: [] });
  const cachedRectsRef = useRef({ root: null, grid: null });
  const hoverPanelRef = useRef(null);
  const tooltipRowRefs = useRef({});
  const hoverIndexRef = useRef(null);

  // Ổn định default khi refetch API
  const prevTimelineSeriesKeysRef = useRef(new Set());
  const lastFilterAppliedDateRangeRef = useRef({ startDate: "", endDate: "" });

  const todayIsoDate = useMemo(() => getTodayIsoDate(), []);

  const timelineStatsMode = state.detailChartMode === "dvvc" ? "dvvc" : "status";
  const { data: orderStatusList, refetch: refetchOrderStatuses } = useOrderStatus();

  useEffect(() => {
    refetchOrderStatuses();
  }, []);

  const {
    hubSelectOptions,
    isLoading: isHubsListLoading,
  } = useHubMultiSelectOptions({
    queryKey: "dashboard-order-mgmt-hubs",
  });

  // Tham số hub + loại đơn cho API KPI tháng
  const kpiPerformanceStatsBaseParams = useMemo(() => {
    const hub_id = getDashboardHubFilterIds({
      selectedHubIds: state.selectedHubIds,
      hubSelectOptions,
    });
    if (!hub_id) {
      return null;
    }
    return {
      hub_id,
      order_type: resolveDashboardOrderTypeParam(state.selectedOrderTypes),
    };
  }, [state.selectedHubIds, state.selectedOrderTypes, hubSelectOptions]);

  const statusFilterOptions = useMemo(() => {
    const normalized = (orderStatusList || [])
      .map(statusItem => ({
        value: String(statusItem?.value ?? statusItem?.status ?? ""),
        label:
          typeof statusItem?.label === "string"
            ? statusItem.label
            : String(statusItem?.label ?? "").trim(),
      }))
      .filter(statusItem => /^\d+$/.test(statusItem.value) && statusItem.label !== "");

    if (normalized.length) {
      return normalized;
    }

    return FALLBACK_STATUS_FILTER_OPTIONS;
  }, [orderStatusList]);

  const selectedStatusValue = useMemo(() => {
    if (state.selectedStatusFilter === "all") {
      return "all";
    }
    const targetValue = String(state.selectedStatusFilter);
    const isValidSelectedStatus = statusFilterOptions.some(statusItem => statusItem.value === targetValue);
    return isValidSelectedStatus ? targetValue : "all";
  }, [state.selectedStatusFilter, statusFilterOptions]);

  const statusLabelByValue = useMemo(
    () =>
      Object.fromEntries(
        (statusFilterOptions || []).map(statusItem => [
          String(statusItem.value),
          statusItem.label,
        ])
      ),
    [statusFilterOptions]
  );

  // Hiển thị tối đa 7 ngày khi line chart full status
  const areAllDefaultHiddenStatusesVisible = useMemo(() => {
    const defaultHiddenKeys = statusFilterOptions
      .map(optionItem => String(optionItem.value))
      .filter(valueItem => isStatusChartKeyHiddenByDefault(valueItem, statusLabelByValue));
    if (!defaultHiddenKeys.length) {
      return true;
    }
    const visibleKeySet = new Set(state.visibleKeys);
    return defaultHiddenKeys.every(keyItem => visibleKeySet.has(keyItem));
  }, [statusFilterOptions, statusLabelByValue, state.visibleKeys]);

  const defaultHiddenStatusKeys = useMemo(
    () =>
      statusFilterOptions
        .map(optionItem => String(optionItem.value))
        .filter(valueItem => isStatusChartKeyHiddenByDefault(valueItem, statusLabelByValue)),
    [statusFilterOptions, statusLabelByValue]
  );

  const shouldUseSevenDayStatusWindow = useMemo(
    () =>
      state.detailChartMode === "status"
      && selectedStatusValue === "all"
      && (areAllDefaultHiddenStatusesVisible || state.shouldForceSevenDayStatusRange),
    [
      state.detailChartMode,
      selectedStatusValue,
      areAllDefaultHiddenStatusesVisible,
      state.shouldForceSevenDayStatusRange,
    ]
  );

  const defaultRange = useMemo(() => {
    if (state.selectedStatusFilter !== "all") {
      return getLastNDaysRange(todayIsoDate, 90);
    }
    if (state.detailChartMode === "status" && !shouldUseSevenDayStatusWindow) {
      return getLastNDaysRange(todayIsoDate, MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS);
    }
    // DVVC: Mặc định hiển thị từ ngày có đơn chưa hoàn tất
    if (state.detailChartMode === "dvvc") {
      return getLastNDaysRange(todayIsoDate, MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS);
    }
    return getLast7DaysRange(todayIsoDate);
  }, [
    state.selectedStatusFilter,
    state.detailChartMode,
    todayIsoDate,
    shouldUseSevenDayStatusWindow,
  ]);

  const shouldForceDefaultSevenDayApiRange = useMemo(
    () =>
      !state.hasManualDateRange
      && shouldUseSevenDayStatusWindow,
    [
      state.hasManualDateRange,
      shouldUseSevenDayStatusWindow,
    ]
  );

  const apiRequestDateRange = useMemo(() => {
    if (state.hasManualDateRange) {
      return state.manualDateRange;
    }

    if (shouldForceDefaultSevenDayApiRange) {
      return defaultRange;
    }

    return { startDate: "", endDate: "" };
  }, [
    state.hasManualDateRange,
    state.manualDateRange,
    shouldForceDefaultSevenDayApiRange,
    defaultRange,
  ]);

  useEffect(() => {
    if (
      state.shouldForceSevenDayStatusRange
      && (state.detailChartMode !== "status" || selectedStatusValue !== "all")
    ) {
      setState(prevState => ({ ...prevState, shouldForceSevenDayStatusRange: false }));
    }
  }, [
    state.shouldForceSevenDayStatusRange,
    state.detailChartMode,
    selectedStatusValue,
  ]);

  const statsBaseParams = useMemo(() => {
    const statusIdsForApi =
      selectedStatusValue === "all"
      ? statusFilterOptions.map(statusItem => String(statusItem.value))
        : [String(selectedStatusValue)];

    if (!statusIdsForApi.length) {
      return null;
    }

    const params = {
      status: statusIdsForApi.join(","),
    };

    params.hub_id = getDashboardHubFilterIds({
      selectedHubIds: state.selectedHubIds,
      hubSelectOptions,
    });
    if (!params.hub_id) {
      return null;
    }

    params.order_type = resolveDashboardOrderTypeParam(state.selectedOrderTypes);

    return params;
  }, [
    state.selectedHubIds,
    state.selectedOrderTypes,
    selectedStatusValue,
    statusFilterOptions,
    hubSelectOptions,
  ]);

  const shouldFetchTimelineStats =
    statsBaseParams !== null &&
    (selectedStatusValue !== "all" || statusFilterOptions.length > 0);

  const {
    mergedRows: apiTimelineRows,
    isFetching: isTimelineStatsFetching,
    isLoading: isTimelineStatsLoading,
    isDateRangeParamsEnabled,
    isTimelineStatsRangeTooLong,
    filterAppliedDateRange,
  } =
    useDashboardDailyTimelineStats({
      mode: timelineStatsMode,
      baseParams: statsBaseParams,
      orderStatusList: statusFilterOptions,
      enabled: shouldFetchTimelineStats,
      dateRange: apiRequestDateRange,
      useDateRangeParams: state.hasManualDateRange || shouldForceDefaultSevenDayApiRange,
      isManualDateRange: state.hasManualDateRange,
    });

  const effectiveDateRange = useMemo(() => {
    if (state.hasManualDateRange) {
      return state.manualDateRange;
    }

    if (filterAppliedDateRange?.startDate && filterAppliedDateRange?.endDate) {
      return filterAppliedDateRange;
    }

    const cachedApiDateRange = lastFilterAppliedDateRangeRef.current;
    if (cachedApiDateRange?.startDate && cachedApiDateRange?.endDate) {
      return cachedApiDateRange;
    }

    return { startDate: "", endDate: "" };
  }, [
    state.hasManualDateRange,
    state.manualDateRange,
    filterAppliedDateRange,
  ]);

  useEffect(() => {
    if (filterAppliedDateRange?.startDate && filterAppliedDateRange?.endDate) {
      lastFilterAppliedDateRangeRef.current = filterAppliedDateRange;
    }
  }, [filterAppliedDateRange]);

  // Call xong api mới hiển thị chart
  const isTimelineStatsSectionLoading =
    isHubsListLoading
    || (shouldFetchTimelineStats && (isTimelineStatsFetching || isTimelineStatsLoading));

  // Mặc định dùng ngày BE trả về, user chọn range thì theo range đó
  const filteredTimelineData = useMemo(() => {
    if (!isDateRangeParamsEnabled) {
      return apiTimelineRows;
    }
    const hasValidRange =
      Boolean(effectiveDateRange?.startDate)
      && Boolean(effectiveDateRange?.endDate);
    if (!hasValidRange) {
      return apiTimelineRows;
    }
    return filterDateRange(apiTimelineRows, effectiveDateRange);
  }, [apiTimelineRows, effectiveDateRange, isDateRangeParamsEnabled]);

  const statusKeysOrdered = useMemo(() => {
    if (state.detailChartMode === "status" && selectedStatusValue === "all" && statusFilterOptions.length) {
      return statusFilterOptions.map(statusItem => String(statusItem.value));
    }

    const ordered = [];
    const seen = new Set();
    filteredTimelineData.forEach(dayRow => {
      Object.keys(dayRow.statusCounts || {}).forEach(statusKey => {
        if (!seen.has(statusKey)) {
          seen.add(statusKey);
          ordered.push(statusKey);
        }
      });
    });
    return ordered;
  }, [
    filteredTimelineData,
    state.detailChartMode,
    selectedStatusValue,
    statusFilterOptions,
  ]);

  const carrierKeysOrdered = useMemo(() => {
    const ordered = [];
    const seen = new Set();
    filteredTimelineData.forEach(dayRow => {
      Object.keys(dayRow.carrierCounts || {}).forEach(carrierKey => {
        if (!seen.has(carrierKey)) {
          seen.add(carrierKey);
          ordered.push(carrierKey);
        }
      });
    });
    return ordered;
  }, [filteredTimelineData]);

  const timelineCategories = useMemo(
    () => filteredTimelineData.map(item => formatDateLabel(item.date)),
    [filteredTimelineData]
  );

  const dvvcLabelByKey = useMemo(
    () => Object.fromEntries(DVVC_LABELS.map(dvvcItem => [dvvcItem.key, dvvcItem.label])),
    []
  );

  const timelineSeriesSource = useMemo(() => {
    if (state.detailChartMode === "dvvc") {
      return carrierKeysOrdered.map(carrierKey => ({
        key: String(carrierKey),
        name: dvvcLabelByKey[String(carrierKey)] || String(carrierKey),
        data: filteredTimelineData.map(dayRow => dayRow.carrierCounts[String(carrierKey)] || 0),
      }));
    }

    return statusKeysOrdered.map(statusKey => ({
      key: String(statusKey),
      name: statusLabelByValue[String(statusKey)] || String(statusKey),
      data: filteredTimelineData.map(dayRow => dayRow.statusCounts[String(statusKey)] || 0),
    }));
  }, [
    filteredTimelineData,
    state.detailChartMode,
    carrierKeysOrdered,
    statusKeysOrdered,
    dvvcLabelByKey,
    statusLabelByValue,
  ]);

  useEffect(() => {
    const allSeriesKeys = timelineSeriesSource.map(seriesItem => String(seriesItem.key));
    if (!allSeriesKeys.length) {
      return;
    }
    const isStatusMode = state.detailChartMode === "status";
    const isFullStatusSelection = selectedStatusValue === "all";
    const shouldApplyDefaultHiddenStatuses =
      isStatusMode && isFullStatusSelection && allSeriesKeys.length > 0;

    const prevAllKeys = prevTimelineSeriesKeysRef.current;

    setState(prevState => {
      const previousKeys = prevState.visibleKeys || [];
      const prevVisible = new Set(previousKeys);

      let nextVisibleKeys;
      if (!shouldApplyDefaultHiddenStatuses) {
        nextVisibleKeys = allSeriesKeys;
      } else {
        nextVisibleKeys = allSeriesKeys.filter(keyItem => {
          const hiddenDefault = isStatusChartKeyHiddenByDefault(keyItem, statusLabelByValue);
          const wasVisible = prevVisible.has(keyItem);

          if (hiddenDefault) {
            return wasVisible;
          }
          if (!prevAllKeys.has(keyItem)) {
            return true;
          }
          return wasVisible;
        });
      }

      const hasSameLength = previousKeys.length === nextVisibleKeys.length;
      const isSameKeys =
        hasSameLength && previousKeys.every((keyItem, index) => keyItem === nextVisibleKeys[index]);

      if (isSameKeys) {
        return prevState;
      }

      return { ...prevState, visibleKeys: nextVisibleKeys };
    });

    prevTimelineSeriesKeysRef.current = new Set(allSeriesKeys);
  }, [
    timelineSeriesSource,
    state.detailChartMode,
    selectedStatusValue,
    statusLabelByValue,
  ]);

  const visibleSet = useMemo(() => new Set(state.visibleKeys), [state.visibleKeys]);

  const timelineSeries = useMemo(
    () => timelineSeriesSource.filter(seriesItem => visibleSet.has(seriesItem.key)),
    [timelineSeriesSource, visibleSet]
  );

  // Ghi nhận giá trị lớn nhất theo trạng thái hoặc dvvc đang hiển thị
  const timelineRawDataMax = useMemo(() => {
    const visibleValues = timelineSeries
      .flatMap(seriesItem => seriesItem.data || [])
      .map(value => Number(value) || 0);
    return visibleValues.length ? Math.max(...visibleValues) : 0;
  }, [timelineSeries]);

  // Làm tròn giá trị lớn nhất trên chart
  const timelineYAxisMax = useMemo(
    () => computeNiceYAxisBounds(timelineRawDataMax).max,
    [timelineRawDataMax]
  );

  const [hoveredDataPointIndex, setHoveredDataPointIndex] = useState(null);
  const [hoverGuide, setHoverGuide] = useState(null);
  const [leaderSegments, setLeaderSegments] = useState([]);
  const [resizeRevision, setResizeRevision] = useState(0);
  const [overlayDims, setOverlayDims] = useState({ width: 0, height: 0 });
  const [hoverPanelLeft, setHoverPanelLeft] = useState(16);
  const [hoverPanelTop, setHoverPanelTop] = useState(8);
  const [hoveredTooltipKey, setHoveredTooltipKey] = useState(null);

  const legendTotals = useMemo(() => {
    const map = {};
    timelineSeriesSource.forEach(s => {
      map[s.key] = (s.data || []).reduce((sum, value) => sum + Number(value || 0), 0);
    });
    return map;
  }, [timelineSeriesSource]);

  const legendRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < timelineSeriesSource.length; i += LEGEND_COLS) {
      rows.push(timelineSeriesSource.slice(i, i + LEGEND_COLS));
    }
    return rows;
  }, [timelineSeriesSource]);

  const legendDisplayNumbers = useMemo(() => {
    if (hoveredDataPointIndex === null) {
      return legendTotals;
    }
    const map = {};
    timelineSeriesSource.forEach(s => {
      map[s.key] = Number(s.data[hoveredDataPointIndex] ?? 0);
    });
    return map;
  }, [hoveredDataPointIndex, legendTotals, timelineSeriesSource]);

  const timelineLineColors = useMemo(
    () => timelineSeries.map(seriesItem => getTrendLineColorForLabel(seriesItem.key)),
    [timelineSeries]
  );

  useEffect(() => {
    timelineModelRef.current = {
      categories: timelineCategories,
      keys: timelineSeries.map(item => item.key),
    };
  }, [timelineCategories, timelineSeries]);

  const updateCachedRects = useCallback(() => {
    const timelineMountElement = timelineMountRef.current;
    const chartWrapperElement = chartWrapperRef.current;
    if (!timelineMountElement || !chartWrapperElement) {
      cachedRectsRef.current = { root: null, grid: null };
      return;
    }

    const gridElement = chartWrapperElement.querySelector(".apexcharts-grid");
    cachedRectsRef.current = {
      root: timelineMountElement.getBoundingClientRect(),
      grid: gridElement ? gridElement.getBoundingClientRect() : null,
    };
  }, []);

  const handleLegendToggle = useCallback(key => {
    setState(prevState => {
      const isAddingSeriesKey = !prevState.visibleKeys.includes(key);
      const nextVisibleKeys = isAddingSeriesKey
        ? [...prevState.visibleKeys, key]
        : prevState.visibleKeys.filter(item => item !== key);

      const isStatusAllMode =
        prevState.detailChartMode === "status"
        && selectedStatusValue === "all";
      const nextShouldForceSevenDayStatusRange =
        isStatusAllMode
        && defaultHiddenStatusKeys.length > 0
        && defaultHiddenStatusKeys.every(keyItem => nextVisibleKeys.includes(keyItem));

      // Bật lại Hoàn tất/Hủy trên chart → reset về 7 ngày mặc định, xóa range
      const shouldResetDateRangeToDefaultSevenDays =
        prevState.detailChartMode === "status"
        && selectedStatusValue === "all"
        && isAddingSeriesKey
        && isStatusChartKeyHiddenByDefault(key, statusLabelByValue);

      if (!shouldResetDateRangeToDefaultSevenDays) {
        return {
          ...prevState,
          visibleKeys: nextVisibleKeys,
          shouldForceSevenDayStatusRange: nextShouldForceSevenDayStatusRange,
        };
      }

      return {
        ...prevState,
        visibleKeys: nextVisibleKeys,
        hasManualDateRange: false,
        manualDateRange: { startDate: "", endDate: "" },
        shouldForceSevenDayStatusRange: nextShouldForceSevenDayStatusRange,
      };
    });
  }, [selectedStatusValue, statusLabelByValue, defaultHiddenStatusKeys]);

  const handleLegendClick = useCallback(event => {
    const key = event.currentTarget.getAttribute("data-key");
    if (!key) {
      return;
    }
    handleLegendToggle(key);
  }, [handleLegendToggle]);

  const handleDetailChartModeChange = useCallback(event => {
    const detailChartMode = event.target.value;
    setState(prevState => ({
      ...prevState,
      detailChartMode,
      shouldForceSevenDayStatusRange:
        detailChartMode === "status" ? prevState.shouldForceSevenDayStatusRange : false,
    }));
  }, []);

  const handleHubFilterChange = useCallback(selectedHubIds => {
    setState(prevState => ({
      ...prevState,
      selectedHubIds: Array.isArray(selectedHubIds) ? selectedHubIds : [],
    }));
  }, []);

  const handleOrderTypeFilterChange = useCallback(selectedOrderTypes => {
    setState(prevState => ({ ...prevState, selectedOrderTypes }));
  }, []);

  const handleDateRangeFilterChange = useCallback(({ startDate, endDate }) => {
    const nextRange = {
      startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
      endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
    };
    setState(prevState => ({
      ...prevState,
      manualDateRange: nextRange,
      hasManualDateRange: Boolean(nextRange.startDate && nextRange.endDate),
    }));
  }, []);

  const handleTimelinePointSelection = useCallback((event, chartContext, config) => {
    const key = timelineModelRef.current.keys[config.seriesIndex];
    const selectedDate = filteredTimelineData[config.dataPointIndex]?.date || "";
    if (typeof handleNavigateDetails === "function") {
      handleNavigateDetails({
        key,
        date: selectedDate,
        type: state.detailChartMode,
      });
    }
  }, [handleNavigateDetails, filteredTimelineData, state.detailChartMode]);

  const hoverPanelModel = useMemo(() => {
    if (hoveredDataPointIndex === null || !timelineCategories.length) {
      return null;
    }
    const rows = timelineSeries.map((s, idx) => ({
      key: s.key,
      name: s.name,
      value: Number(s.data[hoveredDataPointIndex] ?? 0),
      color: timelineLineColors[idx],
      seriesIdx: idx,
    }));
    rows.sort((a, b) => {
      if (b.value !== a.value) {
        return b.value - a.value;
      }
      return String(a.name).localeCompare(String(b.name), "vi");
    });
    return { rows };
  }, [
    hoveredDataPointIndex,
    timelineSeries,
    timelineLineColors,
  ]);

  const handleTimelineMouseMove = useCallback((_event) => {
    const cursorX = _event?.clientX;
    const cursorY = _event?.clientY;
    if (typeof cursorX !== "number" || typeof cursorY !== "number") {
      return;
    }

    const eventTarget = _event?.target;
    if (
      hoverPanelRef.current
      && eventTarget instanceof Element
      && hoverPanelRef.current.contains(eventTarget)
    ) {
      return;
    }

    const rootRect = cachedRectsRef.current.root;
    const gridRect = cachedRectsRef.current.grid;
    if (!rootRect || !gridRect) {
      if (hoverIndexRef.current !== null) {
        hoverIndexRef.current = null;
        setHoveredDataPointIndex(null);
      }
      setHoverGuide(prevGuide => (prevGuide ? null : prevGuide));
      return;
    }

    const tooltipPanelBounds = hoverPanelRef.current?.getBoundingClientRect();
    const chartTooltipUnionBounds = getChartGridTooltipUnionBounds(gridRect, tooltipPanelBounds);
    const isInsideChartTooltipUnion =
      hoverIndexRef.current !== null
      && isPointInsideScreenBounds(chartTooltipUnionBounds, cursorX, cursorY);

    const isInsidePlotArea =
      cursorX >= gridRect.left
      && cursorX <= gridRect.right
      && cursorY >= (gridRect.top - 6)
      && cursorY <= (gridRect.bottom + 24);

    if (!isInsidePlotArea) {
      if (isInsideChartTooltipUnion) {
        return;
      }
      if (hoverIndexRef.current !== null) {
        hoverIndexRef.current = null;
        setHoveredDataPointIndex(null);
      }
      setHoverGuide(prevGuide => (prevGuide ? null : prevGuide));
      return;
    }

    const pointCount = Math.max(timelineCategories.length, 1);
    const stepX = pointCount > 1 ? gridRect.width / (pointCount - 1) : 0;
    const xWithinGrid = Math.max(0, Math.min(gridRect.width, cursorX - gridRect.left));
    const idx = resolveTimelineMilestoneIndex(
      xWithinGrid,
      stepX,
      pointCount,
      hoverIndexRef.current,
      hoveredDataPointIndex !== null
    );

    if (idx === null) {
      if (hoverIndexRef.current !== null) {
        hoverIndexRef.current = null;
        setHoveredDataPointIndex(null);
      }
      setHoverGuide(prevGuide => (prevGuide ? null : prevGuide));
      return;
    }

    if (hoverIndexRef.current !== idx) {
      hoverIndexRef.current = idx;
      setHoveredDataPointIndex(idx);
    }

    // Xử lý giá trị đường line hiển thị trên trục Y
    const chartYMax = Math.max(1, timelineYAxisMax);
    const yRatio = (gridRect.bottom - cursorY) / Math.max(gridRect.height, 1);
    const interpolatedValue = Math.max(0, Math.min(chartYMax, yRatio * chartYMax));
    const interpolatedValueInt = Math.round(interpolatedValue);
    const markerX = cursorX - rootRect.left;
    const markerY = cursorY - rootRect.top;

    setHoverGuide(prevGuide => {
      const hasNoMaterialChanges =
        prevGuide
        && prevGuide.index === idx
        && Math.abs(prevGuide.x - markerX) < 0.2
        && Math.abs(prevGuide.y - markerY) < 0.2
        && prevGuide.value === interpolatedValueInt;
      if (hasNoMaterialChanges) {
        return prevGuide;
      }
      return {
        index: idx,
        x: markerX,
        y: markerY,
        value: interpolatedValueInt,
      };
    });
  }, [hoveredDataPointIndex, timelineCategories.length, timelineYAxisMax]);

  const handleTimelineMouseEnter = useCallback(() => {
    updateCachedRects();
  }, [updateCachedRects]);

  const handleTimelineMouseLeave = useCallback(() => {
    hoverIndexRef.current = null;
    setHoveredDataPointIndex(null);
    setHoverGuide(prevGuide => (prevGuide ? null : prevGuide));
  }, []);

  useEffect(() => {
    const onResize = () => setResizeRevision(r => r + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      updateCachedRects();
    };

    // Bắt scroll từ container lồng nhau
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [updateCachedRects]);

  useLayoutEffect(() => {
    const el = timelineMountRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const sync = () => {
      setOverlayDims({ width: el.offsetWidth, height: el.offsetHeight });
      updateCachedRects();
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isTimelineStatsSectionLoading, updateCachedRects]);

  useLayoutEffect(() => {
    updateCachedRects();
  }, [updateCachedRects, timelineCategories.length, timelineSeries.length, timelineLineColors]);

  useLayoutEffect(() => {
    if (
      hoveredDataPointIndex === null
      || !hoverPanelModel
      || !timelineMountRef.current
      || !chartWrapperRef.current
    ) {
      setLeaderSegments(prevSegments => (prevSegments.length ? [] : prevSegments));
      return;
    }
    const rootRect = timelineMountRef.current.getBoundingClientRect();
    const gridEl = chartWrapperRef.current.querySelector(".apexcharts-grid");
    const gridRect = gridEl?.getBoundingClientRect();
    const dataPointCount = Math.max(timelineCategories.length, 1);
    const rightCount = Math.ceil(dataPointCount / 2);
    const shouldPlaceTooltipLeft = hoveredDataPointIndex >= rightCount;
    const panelWidth = hoverPanelRef.current?.getBoundingClientRect().width || 185;
    const panelHeight = hoverPanelRef.current?.getBoundingClientRect().height || 120;
    const chartYMax = Math.max(1, timelineYAxisMax);
    const nextSegments = [];
    for (let r = 0; r < hoverPanelModel.rows.length; r += 1) {
      const row = hoverPanelModel.rows[r];
      const rowEl = tooltipRowRefs.current[row.key];
      if (!rowEl) {
        continue;
      }
      const rRect = rowEl.getBoundingClientRect();
      const stepX = dataPointCount > 1 ? gridRect.width / (dataPointCount - 1) : 0;
      const x1 = gridRect.left - rootRect.left + stepX * hoveredDataPointIndex;
      const clampedValue = Math.max(0, Math.min(chartYMax, Number(row.value || 0)));
      const y1 = gridRect.bottom - rootRect.top - (clampedValue / chartYMax) * gridRect.height;
      const x2 = shouldPlaceTooltipLeft
        ? rRect.right - rootRect.left
        : rRect.left - rootRect.left;
      const y2 = rRect.top - rootRect.top + rRect.height / 2;
      nextSegments.push({
        key: row.key,
        color: row.color,
        x1,
        y1,
        x2,
        y2,
      });
    }
    if (nextSegments.length) {
      const avgX = nextSegments.reduce((sum, seg) => sum + seg.x1, 0) / nextSegments.length;
      const desiredLeft = shouldPlaceTooltipLeft
        ? avgX - panelWidth - 8
        : avgX + 8;
      const safeLeft = Math.max(8, Math.min(desiredLeft, rootRect.width - panelWidth - 8));
      const anchorY = nextSegments[0]?.y1 ?? rootRect.height / 2;
      const desiredTop = anchorY - panelHeight / 2;
      const safeTop = Math.max(8, Math.min(desiredTop, rootRect.height - panelHeight - 8));
      const hasLeftChanges = Math.abs(safeLeft - hoverPanelLeft) > 0.5;
      const hasTopChanges = Math.abs(safeTop - hoverPanelTop) > 0.5;
      if (hasLeftChanges || hasTopChanges) {
        setHoverPanelLeft(safeLeft);
        setHoverPanelTop(safeTop);
        return;
      }
    }
    setLeaderSegments(nextSegments);
  }, [
    hoveredDataPointIndex,
    hoverPanelModel,
    hoverPanelLeft,
    hoverPanelTop,
    resizeRevision,
    timelineCategories.length,
    timelineYAxisMax,
  ]);

  const timelineOptions = useMemo(
    () =>
      buildTimelineChartOptions({
        categories: timelineCategories,
        onPointSelect: handleTimelinePointSelection,
        colorPalette: timelineLineColors,
        onChartMouseMove: handleTimelineMouseMove,
        dataMax: timelineRawDataMax,
      }),
    [
      handleTimelineMouseMove,
      handleTimelinePointSelection,
      timelineCategories,
      timelineLineColors,
      timelineRawDataMax,
    ]
  );

  const isHoverPanelLeft = useMemo(() => {
    if (hoveredDataPointIndex === null) {
      return false;
    }
    const dataPointCount = Math.max(timelineCategories.length, 1);
    const rightCount = Math.ceil(dataPointCount / 2);
    return hoveredDataPointIndex >= rightCount;
  }, [hoveredDataPointIndex, timelineCategories.length]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Dashboard</title>
        </MetaTags>
        <Container fluid>
          <BreadcrumbWithLink title="Dashboard" style={{ marginBottom: "0px" }} />
          {/* Biểu đồ KPI tháng */}
          <div className="py-2 pb-0">
            <KpiPerformanceBarChart
              isParentLoading={isHubsListLoading}
              kpiStatsBaseParams={kpiPerformanceStatsBaseParams}
              hubSelectOptions={hubSelectOptions}
              selectedHubIds={state.selectedHubIds}
              selectedOrderTypes={state.selectedOrderTypes}
              onHubFilterChange={handleHubFilterChange}
              onOrderTypeFilterChange={handleOrderTypeFilterChange}
            />
          </div>
          <div className="py-2">
            <div className="card h-100">
              <div className="card-body">
                <div className="dashboardFilterBar">
                  <div>
                    <DateRangePicker
                      numberOfMonths={1}
                      startDate={effectiveDateRange.startDate ? moment(effectiveDateRange.startDate, "YYYY-MM-DD") : null}
                      startDateId="dashboard_order_mgmt_start"
                      endDate={effectiveDateRange.endDate ? moment(effectiveDateRange.endDate, "YYYY-MM-DD") : null}
                      endDateId="dashboard_order_mgmt_end"
                      startDatePlaceholderText="Ngày bắt đầu"
                      endDatePlaceholderText="Ngày kết thúc"
                      isOutsideRange={() => false}
                      onDatesChange={handleDateRangeFilterChange}
                      focusedInput={focusedInput}
                      onFocusChange={nextFocusedInput => setFocusedInput(nextFocusedInput)}
                      displayFormat="DD/MM/YYYY"
                      minimumNights={0}
                      hideKeyboardShortcutsPanel
                    />
                  </div>
                  {/*
                  <Dropdown isOpen={isHubDropdownOpen} toggle={() => setIsHubDropdownOpen(prev => !prev)}>
                    <DropdownToggle className="btn primary-btn text-overflow" caret>
                      Hub: {state.selectedHubId ? hubSelectOptions.find(opt => opt.value === state.selectedHubId)?.label : "Tất cả"}
                    </DropdownToggle>
                    <DropdownMenu>
                      {hubSelectOptions.map(hubOption => (
                        <DropdownItem key={hubOption.value} onClick={() => handleHubFilterChange(hubOption.value)}>
                          {hubOption.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                  <Dropdown isOpen={isOrderTypeDropdownOpen} toggle={() => setIsOrderTypeDropdownOpen(prev => !prev)}>
                    <DropdownToggle className="btn primary-btn text-overflow" caret>
                      Loại đơn: {state.selectedOrderType ? ORDER_TYPE_FILTER_OPTIONS.find(opt => opt.value === state.selectedOrderType)?.label : "Tất cả"}
                    </DropdownToggle>
                    <DropdownMenu>
                      {ORDER_TYPE_FILTER_OPTIONS.map(typeOption => (
                        <DropdownItem key={typeOption.value} onClick={() => handleOrderTypeFilterChange(typeOption.value)}>
                          {typeOption.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                  */}
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h5 className="text-primary mb-0 fw-bold">Thống kê đơn hàng trong kỳ</h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <select
                      className="form-select form-select-sm border-primary text-primary"
                      style={{ minWidth: "260px", maxWidth: "320px" }}
                      aria-label="Chế độ thống kê chi tiết đơn hàng"
                      value={state.detailChartMode}
                      onChange={handleDetailChartModeChange}
                    >
                      <option value="status">Thống kê đơn hàng theo trạng thái</option>
                      <option value="dvvc">Thống kê đơn hàng theo DVVC</option>
                    </select>
                  </div>
                </div>

                {isTimelineStatsSectionLoading ? (
                  <div className="timelineLoadingBlock">
                    <div className="spinner-border text-primary" role="status" style={{ width: "2rem", height: "2rem" }}>
                      <span className="visually-hidden">Đang tải dữ liệu thống kê...</span>
                    </div>
                    <span className="text-primary small mt-2">Đang tải dữ liệu thống kê...</span>
                  </div>
                ) : isTimelineStatsRangeTooLong ? (
                  <div className="timelineLoadingBlock">
                    <div
                      className="alert alert-warning border border-warning shadow-sm mb-0 px-4 py-3 text-start"
                      role="alert"
                      style={{ maxWidth: "860px" }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="26"
                          height="26"
                          viewBox="0 0 24 24"
                          fill="#b45309"
                          aria-hidden="true"
                          className="flex-shrink-0"
                        >
                          <path d="M12 2.25a1.5 1.5 0 0 1 1.299.75l9 15.75A1.5 1.5 0 0 1 21 21H3a1.5 1.5 0 0 1-1.299-2.25l9-15.75A1.5 1.5 0 0 1 12 2.25zm0 5.25a1 1 0 0 0-1 1V13a1 1 0 1 0 2 0V8.5a1 1 0 0 0-1-1zm0 10a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5z" />
                        </svg>
                        <p className="mb-0 fs-6 text-dark">
                          <span className="d-block">Khoảng thời gian thống kê đang vượt quá 15 ngày.</span>
                          <span className="d-block">Vui lòng chọn khoảng ngày tối đa 15 ngày (tính cả ngày bắt đầu và ngày kết thúc) để xem biểu đồ.</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-3 legendGrid">
                      {legendRows.map((row, rowIndex) => (
                        <div className="row g-2 mb-1 justify-content-center" key={`legend-row-${rowIndex}`}>
                          {row.map(item => {
                            const colorDot = getTrendLineColorForLabel(item.key);
                            const total = legendDisplayNumbers[item.key] ?? 0;
                            const isVisible = visibleSet.has(item.key);
                            return (
                              <div className="col-auto" key={item.key}>
                                <button
                                  type="button"
                                  className={`btn btn-sm text-start d-flex align-items-center gap-2 py-2 border rounded legendToggleButton ${isVisible ? "bg-white" : "bg-light opacity-75"
                                    }`}
                                  data-key={item.key}
                                  onClick={handleLegendClick}
                                  style={{ minWidth: "250px" }}
                                >
                                  <div
                                    className="position-relative flex-shrink-0"
                                    style={{ width: "34px", height: "12px" }}
                                  >
                                    <span
                                      className="position-absolute"
                                      style={{
                                        left: "1px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        width: "32px",
                                        height: "3px",
                                        borderRadius: "2px",
                                        backgroundColor: colorDot,
                                      }}
                                    />
                                    <span
                                      className="position-absolute rounded-circle bg-white"
                                      style={{
                                        width: "10px",
                                        height: "10px",
                                        borderColor: colorDot,
                                        borderStyle: "solid",
                                        borderWidth: "2px",
                                        left: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                      }}
                                    />
                                  </div>
                                  <span className="text-truncate flex-grow-1 text-dark">{item.name}</span>
                                  <span className="fw-semibold flex-shrink-0 text-dark">{total}</span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    <div
                      ref={timelineMountRef}
                      className="position-relative timelineChartWrap"
                      onMouseEnter={handleTimelineMouseEnter}
                      onMouseMove={handleTimelineMouseMove}
                      onMouseLeave={handleTimelineMouseLeave}
                    >
                      <div ref={chartWrapperRef}>
                        <ReactApexChart
                          options={/** @type {any} */ (timelineOptions)}
                          series={timelineSeries}
                          type="line"
                          height={TIMELINE_CHART_HEIGHT_PX}
                        />
                      </div>

                      <svg
                        className="leaderLinesSvg"
                        aria-hidden
                        width={overlayDims.width || "100%"}
                        height={overlayDims.height || "100%"}
                        style={{ overflow: "visible" }}
                      >
                        {hoverGuide && (
                          <g>
                            <line
                              x1={0}
                              y1={hoverGuide.y}
                              x2={overlayDims.width || 0}
                              y2={hoverGuide.y}
                              stroke="#dc3545"
                              strokeWidth={1.2}
                              strokeOpacity={0.45}
                              vectorEffect="non-scaling-stroke"
                            />
                            <rect
                              x={0}
                              y={Math.max(0, hoverGuide.y - 12)}
                              width={34}
                              height={24}
                              fill="#dc3545"
                              rx={2}
                              ry={2}
                            />
                            <polygon
                              points={`34,${hoverGuide.y - 3} 40,${hoverGuide.y} 34,${hoverGuide.y + 3}`}
                              fill="#dc3545"
                            />
                            <text
                              x={17}
                              y={hoverGuide.y + 1}
                              fill="#ffffff"
                              fontSize={12}
                              fontWeight={700}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {formatHoverGuideValue(hoverGuide.value)}
                            </text>
                          </g>
                        )}

                        {leaderSegments.map(seg => (
                          <line
                            key={seg.key}
                            x1={seg.x1}
                            y1={seg.y1}
                            x2={seg.x2}
                            y2={seg.y2}
                            stroke={seg.color}
                            strokeWidth={1.5}
                            strokeOpacity={0.95}
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                      </svg>

                      {hoverPanelModel && (
                        <div
                          ref={hoverPanelRef}
                          className={`position-absolute floatingTooltip ${isHoverPanelLeft ? "floatingTooltipLeft" : "floatingTooltipRight"
                            }`}
                          style={{ left: `${hoverPanelLeft}px`, top: `${hoverPanelTop}px` }}
                        >
                          {hoverPanelModel.rows.map(row => (
                            (() => {
                              const isTooltipRowHovered = hoveredTooltipKey === row.key;
                              const tooltipBackgroundColor = isTooltipRowHovered ? row.color : "#ffffff";
                              const tooltipTitleColor = isTooltipRowHovered ? "rgba(255, 255, 255, 0.95)" : "#212529";
                              const tooltipValueColor = isTooltipRowHovered ? "#ffffff" : "#212529";
                              return (
                                <button
                                  key={row.key}
                                  type="button"
                                  className="mb-1 text-center shadow-sm tooltipOuter"
                                  style={/** @type {any} */ ({
                                    border: `1.5px solid ${row.color}`,
                                    "--tooltip-bg": tooltipBackgroundColor,
                                    "--tooltip-title": tooltipTitleColor,
                                    "--tooltip-value": tooltipValueColor,
                                  })}
                                  ref={el => {
                                    if (el) {
                                      tooltipRowRefs.current[row.key] = el;
                                    } else {
                                      delete tooltipRowRefs.current[row.key];
                                    }
                                  }}
                                  onMouseEnter={() => setHoveredTooltipKey(row.key)}
                                  onMouseLeave={() => setHoveredTooltipKey(prevKey => (prevKey === row.key ? null : prevKey))}
                                  onFocus={() => setHoveredTooltipKey(row.key)}
                                  onBlur={() => setHoveredTooltipKey(prevKey => (prevKey === row.key ? null : prevKey))}
                                  onClick={() => {
                                    if (typeof handleNavigateDetails === "function") {
                                      handleNavigateDetails({
                                        key: row.key,
                                        date: filteredTimelineData[hoveredDataPointIndex]?.date || "",
                                        type: state.detailChartMode,
                                      });
                                    }
                                  }}
                                >
                                  <div className="tooltipSeriesTitle">{row.name}</div>
                                  <div className="tooltipSeriesValue">{row.value}</div>
                                </button>
                              );
                            })()
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
